import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  X,
  MapPin,
  Activity,
  FileText,
  Clock,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  Circle,
  AlertTriangle,
  Upload,
  ExternalLink,
} from 'lucide-react'
import { supabase, isDemoMode } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { useUpdateTrackerProgress } from '@/hooks/useTrackers'
import { getProgressColor, getDisciplineInfo } from '@/lib/constants'
import ProgressBar from '@/components/shared/ProgressBar'
import StatusBadge from '@/components/shared/StatusBadge'
import type { ActivityInstance, SubactivityProgress, ActivityTemplate } from '@/types'

interface ElementPanelProps {
  elementId:   string
  elementType: string
  onClose:     () => void
}

// =============================================================================
// ElementPanel
// =============================================================================

export default function ElementPanel({ elementId, elementType, onClose }: ElementPanelProps) {
  const { canEdit }          = useAuth()
  const updateProgress       = useUpdateTrackerProgress()
  const [expandedFamilies, setExpandedFamilies] = useState<Set<string>>(new Set())

  // ---------------------------------------------------------------------------
  // Fetch tracker polygon + label assignment
  // ---------------------------------------------------------------------------
  const { data: tracker, isLoading: trackerLoading } = useQuery({
    queryKey: ['tracker-panel', elementId],
    queryFn: async () => {
      if (isDemoMode) return {
        id: elementId, tracker_label: elementId,
        parcel: { id: 'parc-03', parcel_number: 3, display_name: 'Parcela 3', name: 'Parc-03' },
        label_assignment: { tracker_label: elementId, pile_count: 4 },
      }
      const { data, error } = await supabase
        .from('tracker_polygons')
        .select(`
          *,
          parcel:parcels(*),
          label_assignment:tracker_label_assignments(*)
        `)
        .eq('id', elementId)
        .maybeSingle()
      if (error) throw error
      return data
    },
    enabled: !!elementId && elementType === 'tracker',
  })

  // ---------------------------------------------------------------------------
  // Fetch activity instances for this parcel
  // ---------------------------------------------------------------------------
  const parcelId = (tracker?.parcel as { id: string } | null)?.id ?? null

  const { data: instances, isLoading: instancesLoading } = useQuery({
    queryKey: ['tracker-activities', parcelId],
    queryFn: async () => {
      if (!parcelId) return []
      if (isDemoMode) return [] // demo: no tracker-level subactivities
      const { data, error } = await supabase
        .from('activity_instances')
        .select(`
          *,
          family:activity_families(*),
          subactivities:subactivity_progress(
            *,
            template:activity_templates(*)
          )
        `)
        .eq('parcel_id', parcelId)
        .eq('is_active', true)
        .order('family(display_order)', { ascending: true })
      if (error) throw error
      return (data ?? []) as ActivityInstance[]
    },
    enabled: !!parcelId,
  })

  // ---------------------------------------------------------------------------
  // Fetch documents for this parcel
  // ---------------------------------------------------------------------------
  const { data: documents } = useQuery({
    queryKey: ['tracker-documents', parcelId],
    queryFn: async () => {
      if (!parcelId || isDemoMode) return []
      const { data, error } = await supabase
        .from('documents')
        .select(`*, uploader:user_profiles(full_name)`)
        .eq('parcel_id', parcelId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(10)
      if (error) throw error
      return data ?? []
    },
    enabled: !!parcelId,
  })

  const toggleFamily = (familyId: string) => {
    setExpandedFamilies((prev) => {
      const next = new Set(prev)
      next.has(familyId) ? next.delete(familyId) : next.add(familyId)
      return next
    })
  }

  const handleSubactivityUpdate = async (
    activityInstanceId: string,
    templateId: string,
    newPct: number
  ) => {
    await updateProgress.mutateAsync({ activityInstanceId, templateId, reportedPct: newPct })
  }

  const trackerCode    = (tracker?.tracker_code) ?? 'Sin código'
  const parcel         = tracker?.parcel as { parcel_number: number; display_name: string } | null
  const labelAssignment = (tracker as { label_assignment?: { tipo: string; fila: string } } | null)?.label_assignment

  const isLoading = trackerLoading || instancesLoading

  return (
    <div className="absolute right-0 top-0 bottom-0 w-96 bg-white border-l border-gray-200 shadow-panel z-20 flex flex-col">
      {/* Header */}
      <div className="flex items-start justify-between px-5 py-4 border-b border-gray-100 shrink-0">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-brand-600 shrink-0" />
            <h3 className="text-sm font-bold text-gray-900 truncate">
              {trackerCode}
            </h3>
          </div>
          {parcel && (
            <p className="text-xs text-gray-500 mt-0.5 ml-6">{parcel.display_name}</p>
          )}
          {labelAssignment && (
            <div className="flex gap-2 ml-6 mt-1">
              {labelAssignment.tipo && (
                <span className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                  {labelAssignment.tipo}
                </span>
              )}
              {labelAssignment.fila && (
                <span className="text-[10px] bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">
                  Fila {labelAssignment.fila}
                </span>
              )}
            </div>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors shrink-0 ml-2"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Tracker info */}
            {tracker && (
              <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <p className="text-gray-400">Zona</p>
                    <p className="font-semibold text-gray-800 mt-0.5">{tracker.zone ?? '—'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Tipo string</p>
                    <p className="font-semibold text-gray-800 mt-0.5">{tracker.string_type ?? '—'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Índice</p>
                    <p className="font-semibold text-gray-800 mt-0.5">#{tracker.polygon_index ?? '—'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Etiquetado</p>
                    <p className={`font-semibold mt-0.5 ${tracker.is_labeled ? 'text-green-600' : 'text-orange-500'}`}>
                      {tracker.is_labeled ? 'Sí' : 'No'}{tracker.label_conflict ? ' ⚠' : ''}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Activity instances */}
            <div className="px-5 py-4">
              <div className="flex items-center gap-2 mb-3">
                <Activity className="w-4 h-4 text-gray-500" />
                <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Actividades ({instances?.length ?? 0})
                </h4>
              </div>

              {instances && instances.length > 0 ? (
                <div className="space-y-2">
                  {instances.map((inst) => {
                    const family    = inst.family as { id: string; code: string; name: string; discipline: string } | null
                    const isExpanded = family ? expandedFamilies.has(family.id) : false
                    const discInfo  = family ? getDisciplineInfo(family.discipline as never) : null
                    const subacts   = (inst.subactivities ?? []) as (SubactivityProgress & { template: ActivityTemplate })[]

                    return (
                      <div key={inst.id} className="border border-gray-100 rounded-lg overflow-hidden">
                        <button
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors"
                          onClick={() => family && toggleFamily(family.id)}
                        >
                          {isExpanded ? (
                            <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                          ) : (
                            <ChevronRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              {discInfo && (
                                <span
                                  className="text-[10px] font-medium px-1.5 py-0.5 rounded-sm"
                                  style={{ backgroundColor: discInfo.bgColor, color: discInfo.color }}
                                >
                                  {discInfo.label}
                                </span>
                              )}
                              <span className="text-xs font-medium text-gray-800 truncate">
                                {family?.name}
                              </span>
                            </div>
                            <ProgressBar value={inst.progress_pct} size="sm" showPercent={false} animated={false} />
                          </div>
                          <div className="shrink-0 ml-2">
                            <span
                              className="text-xs font-bold"
                              style={{ color: getProgressColor(inst.progress_pct) }}
                            >
                              {inst.progress_pct.toFixed(0)}%
                            </span>
                          </div>
                        </button>

                        {/* Subactivities */}
                        {isExpanded && (
                          <div className="bg-gray-50 border-t border-gray-100 divide-y divide-gray-100">
                            {subacts.sort((a, b) =>
                              (a.template?.display_order ?? 0) - (b.template?.display_order ?? 0)
                            ).map((sub) => {
                              const isComplete = sub.reported_pct >= 100

                              return (
                                <div key={sub.id} className="flex items-center gap-2 px-4 py-2">
                                  {isComplete ? (
                                    <CheckCircle className="w-3.5 h-3.5 text-green-500 shrink-0" />
                                  ) : (
                                    <Circle className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                                  )}
                                  <span className="text-xs text-gray-700 flex-1 truncate">
                                    {sub.template?.name ?? `Sub. ${sub.template_id.slice(0, 6)}`}
                                  </span>
                                  {canEdit ? (
                                    <input
                                      type="number"
                                      min="0"
                                      max="100"
                                      value={sub.reported_pct}
                                      onChange={(e) => {
                                        const val = Math.min(100, Math.max(0, parseFloat(e.target.value) || 0))
                                        handleSubactivityUpdate(inst.id, sub.template_id, val)
                                      }}
                                      className="w-14 text-xs text-right border border-gray-200 rounded px-1.5 py-0.5 bg-white"
                                    />
                                  ) : (
                                    <span
                                      className="text-xs font-semibold w-10 text-right"
                                      style={{ color: getProgressColor(sub.reported_pct) }}
                                    >
                                      {sub.reported_pct.toFixed(0)}%
                                    </span>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              ) : (
                <p className="text-xs text-gray-400 text-center py-4">
                  No hay actividades para esta parcela
                </p>
              )}
            </div>

            {/* Documents section */}
            <div className="px-5 py-4 border-t border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-gray-500" />
                  <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Documentos
                  </h4>
                </div>
                {canEdit && (
                  <button className="flex items-center gap-1 text-xs text-brand-600 hover:text-brand-700">
                    <Upload className="w-3 h-3" />
                    Subir
                  </button>
                )}
              </div>

              {documents && documents.length > 0 ? (
                <div className="space-y-2">
                  {documents.map((doc) => (
                    <div key={doc.id} className="flex items-center gap-2 py-1.5">
                      <FileText className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-800 truncate">{doc.filename}</p>
                        <p className="text-[10px] text-gray-400">
                          {doc.document_type} · v{doc.current_version}
                        </p>
                      </div>
                      <StatusBadge status={doc.status as never} type="document" size="sm" />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400 text-center py-2">
                  Sin documentos adjuntos
                </p>
              )}
            </div>

            {/* Validation history */}
            <div className="px-5 pb-6 border-t border-gray-100">
              <div className="flex items-center gap-2 mt-4 mb-3">
                <Clock className="w-4 h-4 text-gray-500" />
                <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Historial
                </h4>
              </div>
              <p className="text-xs text-gray-400 text-center py-2">
                Ver historial completo en la sección de producción
              </p>
              <button className="w-full btn-secondary text-xs justify-center gap-2 mt-2">
                <ExternalLink className="w-3.5 h-3.5" />
                Ver detalle completo
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
